import * as React from 'react';
import { Box, Stack } from '@mui/material';
import useGetWaiverRequests from '../../../hooks/waivers/useGetWaiverRequests';
import { ModalData } from '../../../interfaces';
import Error from '../../global/Error';
import ReuseableTable from '../../table/ReuseableTable';
import { WAIVERS_PAGE_SIZE } from '../../../constants/waivers';
import {
  createRows,
  headerCells,
} from '../../../utils/table/waiverRequestsTable';
import SideBarModal from '../../modal/SideBarModal';
import useWaiverRequestsTableActions from '../../../hooks/waivers/useWaiverRequestsTableActions';
import useSelectWaiver from '../../../hooks/waivers/useSelectWaiver';
import MultiSelectFilter from '../../filtering/MultiSelectFilter';
import useWaiverSubmissionsFilter, {
  ALL_STATUSES,
} from '../../../hooks/waivers/useWaiverSubmissionsFilter';
import { convertSnakeToTitleCase } from '../../../utils/global/helper';
import useGetWaivers from '../../../hooks/waivers/useGetWaivers';

function WaiverSubmissionsSettings() {
  const [activityModalOpen, setActivityModalOpen] = React.useState(false);
  const [activityModalData, setActivityModalData] = React.useState<ModalData>();
  const { publishedWaiver } = useSelectWaiver();
  const { statuses, onChange } = useWaiverSubmissionsFilter();
  const { data: waivers = [] } = useGetWaivers();

  const {
    waiverRequests,
    totalCount,
    isPending,
    isError,
    waiversTablePage,
    enabled,
    sort,
    hasNext,
    hasPrevious,
  } = useGetWaiverRequests({
    waiverId: publishedWaiver?.id,
    statuses,
  });

  const { createMoreActions, setWaiversTablePage } =
    useWaiverRequestsTableActions({
      setModalData: setActivityModalData,
      setModalOpen: setActivityModalOpen,
    });

  const error = isError && 'Error loading waiver requests';

  // Create rows for the table
  const waiverRows = createRows({
    items: waiverRequests,
    createMoreActions,
    waivers,
  });

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: 'calc(100vh - 200px)',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Error errorMessage={error} />
      </Box>
    );
  }

  return (
    <Stack mt={3} gap={3}>
      <MultiSelectFilter
        name="Status"
        options={ALL_STATUSES.map(status => ({
          value: status,
          label: convertSnakeToTitleCase(status),
        }))}
        state={Array.from(statuses)}
        setState={onChange}
        emptyLabel="Select a status"
        allLabel="All statuses"
        sx={{
          width: 175,
        }}
      />

      <ReuseableTable
        name="Waiver Requests"
        rows={waiverRows}
        headerCells={headerCells}
        emptyMessage="No waiver requests to show right now. If you believe this is an error, please reach out to our support team."
        loading={isPending && enabled}
        pagination={{
          count: enabled ? totalCount ?? WAIVERS_PAGE_SIZE : 0,
          rowsPerPage: WAIVERS_PAGE_SIZE,
          colSpan: 4,
          page: waiversTablePage,
          setPage: setWaiversTablePage,
          hasNext,
          hasPrevious,
        }}
        sort={sort}
      />

      {activityModalData ? (
        <SideBarModal
          modalOpen={activityModalOpen}
          setModalOpen={setActivityModalOpen}
          name={activityModalData.name}
          sections={activityModalData.sections}
        />
      ) : null}
    </Stack>
  );
}

export default WaiverSubmissionsSettings;
